import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';
import { Pool } from 'pg';
import { drizzle } from 'drizzle-orm/node-postgres';
import { DatabaseOptions } from '../../types';
import { DatabaseConnection } from '@unidev-hub/database';
import path from 'path';


/**
 * Initialize PostgreSQL database connection with Drizzle ORM
 *
 * @param config Configuration manager
 * @param logger Logger instance
 * @param options Database options
 * @returns Database connection
 */
export async function initializePostgres(
  config: ConfigManager,
  logger: Logger,
  options: DatabaseOptions = {}
): Promise<DatabaseConnection> {
  try {

    // Debug any environment variables that might override config
    logger.debug('Environment variables that might affect database connection:', {
      DATABASE_HOST: process.env.DATABASE_HOST,
      DATABASE_PORT: process.env.DATABASE_PORT,
      DATABASE_USER: process.env.DATABASE_USER || process.env.DB_USER,
      DATABASE_PASSWORD: process.env.DATABASE_PASSWORD || process.env.DB_PASSWORD ? '****' : undefined,
      DATABASE_NAME: process.env.DATABASE_NAME || process.env.DB_NAME
    });

    // Connection details - explicitly log each value being used
    const host = config.get<string>('database.host') || options.connection?.host;
    const port = config.get<number>('database.port') || options.connection?.port;
    const user = config.get<string>('database.user') || options.connection?.username;
    const password = config.get<string>('database.password') || options.connection?.password;
    const database = config.get<string>('database.database') || options.connection?.database;
    const maxConnections = config.get<number>('database.maxConnections', 10) || options.connection?.poolSize;
    const idleTimeout = config.get<number>('database.idleTimeout', 30) * 1000;
    const connectTimeout = config.get<number>('database.connectTimeout', 5) * 1000;


    // Log each value to verify what's being used
    logger.debug('Database connection parameters being used:', {
      host,
      port,
      user,
      password: password ? '****' : undefined,
      database,
      maxConnections,
      source: options.connection ? 'options' : 'config'
    });

    // Create connection config object
    const connectionConfig = {
      host,
      port,
      user,
      password,
      database,
      max: maxConnections,
      idleTimeoutMillis: idleTimeout,
      connectionTimeoutMillis: connectTimeout
    };

    // Log all config keys for debugging
    logger.info('All available database config keys:',
      Object.keys(config.get('database', {})).join(', '));

    // SSL Configuration
    const useSSL = options.connection?.ssl !== undefined
      ? options.connection.ssl
      : config.get<boolean | object>('database.ssl', false);

    logger.debug('SSL configuration:', {
      useSSL: typeof useSSL === 'object' ? 'custom object' : useSSL,
      source: options.connection?.ssl !== undefined ? 'options' : 'config'
    });

    // Determine SSL config based on the configuration
    let sslConfig: boolean | object = false;

    // Auto-detect local connections
    const isLocalConnection =
      connectionConfig.host === 'localhost' ||
      connectionConfig.host === '127.0.0.1';

    logger.info(`Connection type: ${isLocalConnection ? 'local' : 'remote'}`);

    // Set SSL config
    if (useSSL && !isLocalConnection) {
      if (typeof useSSL === 'object') {
        // If SSL is an object with specific settings, use that
        sslConfig = useSSL;
        logger.debug('Using custom SSL configuration for database', {
          options: typeof useSSL === 'object' ? Object.keys(useSSL).join(', ') : 'non-object'
        });
      } else if (useSSL === true) {
        // If SSL is just boolean true, use the configuration that works with Neon
        sslConfig = { rejectUnauthorized: false };
        logger.debug('Using standard SSL configuration for database');
      }
    } else if (isLocalConnection) {
      logger.debug('Automatically disabling SSL for local connection');
      sslConfig = false;
    } else {
      logger.debug('SSL is disabled for database connection');
    }

    // Create a new connection pool
    const poolConfig: any = {
      host: connectionConfig.host,
      port: connectionConfig.port,
      user: connectionConfig.user,
      password: connectionConfig.password,
      database: connectionConfig.database,
      max: connectionConfig.max,
      idleTimeoutMillis: connectionConfig.idleTimeoutMillis,
      connectionTimeoutMillis: connectionConfig.connectionTimeoutMillis
    };

    // Only add SSL config if it's enabled and not a local connection
    if (sslConfig !== false && !isLocalConnection) {
      poolConfig.ssl = sslConfig;
    }

    // Log final connection details
    logger.debug('PostgresSQL connection pool', {
      host: poolConfig.host,
      port: poolConfig.port,
      user: poolConfig.user,
      password: poolConfig.password ? '****' : undefined,
      database: poolConfig.database,
      max: poolConfig.max,
      idleTimeoutMillis: poolConfig.idleTimeoutMillis,
      connectionTimeoutMillis: poolConfig.connectionTimeoutMillis,
      ssl: poolConfig.ssl
    });

    const pool = new Pool(poolConfig);

    // Import the database schema
    let schema: any = null;

    // Try to import the schema if provided in options
    if (options.schema) {
      schema = options.schema;
      logger.info('Using provided database schema for Drizzle ORM');
    } else {
      try {
        // Attempt to dynamically import the schema
        // Look in multiple locations within the project
        const projectRoot = process.cwd();

        // Create an array of valid paths (ensuring no undefined values)
        const possibleSchemaPaths = [
          ...(options.schemaPath ? [options.schemaPath] : []),
          path.join(projectRoot, 'src', 'database', 'schema', 'index.js'),
          path.join(projectRoot, 'src', 'schema', 'index.js'),
          path.join(projectRoot, 'dist', 'database', 'schema', 'index.js'),
          path.join(projectRoot, 'dist', 'schema', 'index.js'),
          path.join(projectRoot, 'schema', 'index.js')
        ];

        logger.debug(`Looking for schema in: ${possibleSchemaPaths.join(', ')}`);

        let schemaFound = false;
        for (const schemaPath of possibleSchemaPaths) {
          try {
            logger.debug(`Trying to import schema from ${schemaPath}`);
            // Ensure we're passing a string to import()
            schema = require(schemaPath);
            logger.debug(`Successfully imported database schema from ${schemaPath}`);
            schemaFound = true;
            break;
          } catch (importError) {
            logger.debug(`Failed to import schema from ${schemaPath}`, {
              error: (importError as Error).message
            });
          }
        }

        if (!schemaFound) {
          logger.warn('Could not find schema in any location, Drizzle ORM will be initialized without schema');
        }
      } catch (error) {
        logger.warn('Failed to import database schema, Drizzle ORM will be initialized without schema', {
          error: (error as Error).message
        });
      }
    }

    // Initialize Drizzle ORM with the schema if available
    const drizzleDB = schema
      ? drizzle(pool, { schema })
      : drizzle(pool);

    // Test the connection
    logger.info('Testing database connection...');
    try {
      const client = await pool.connect();
      logger.info('Connection established, executing test query');
      const result = await client.query('SELECT NOW()');
      client.release();

      logger.info('PostgreSQL connection test successful', {
        timestamp: result.rows[0].now,
        poolSize: connectionConfig.max
      });
    } catch (error) {
      const err = error as Error;

      // Special handling for local connections with SSL errors
      if (isLocalConnection && err.message?.includes('SSL')) {
        logger.error('SSL connection error with local database. SSL should be automatically disabled for local connections.', err);
        throw new Error(`SSL connection failed with local database: ${err.message}. Check your SSL configuration.`);
      } else if (err.message?.includes('SSL')) {
        logger.error('SSL connection error. Try setting sslMode=require in your config', err);
        throw new Error(`SSL connection failed: ${err.message}. Check your SSL configuration.`);
      } else {
        logger.error('Database connection error', err);
        throw new Error(`Database connection failed: ${err.message}`);
      }
    }

    // Store connection configuration for reference
    const dbConfig = {
      type: 'postgres',
      ...connectionConfig,
      // Mask sensitive data
      password: '****'
    };

    // Create a connection object that includes both the raw pool and the Drizzle instance
    const connectionObj = {
      // Original properties
      client: pool,
      // Store the raw pool
      pool: pool,
      // Store the Drizzle ORM instance
      db: drizzleDB,
      // Configuration
      config: dbConfig,

      // Original query method for backward compatibility
      query: async (text: string, params?: any[]) => {
        return await pool.query(text, params || []);
      },

      // Ping method for health checks
      ping: async () => {
        try {
          const pingClient = await pool.connect();
          await pingClient.query('SELECT 1');
          pingClient.release();
          return true;
        } catch (error) {
          logger.error('PostgreSQL ping failed', error as Error);
          return false;
        }
      },

      // Additional method
      beginTransaction: async () => {
        const txClient = await pool.connect();
        await txClient.query('BEGIN');
        return txClient;
      },

      // Disconnect method
      disconnect: async () => {
        await pool.end();
      }
    };

    return connectionObj as unknown as DatabaseConnection;
  } catch (error) {
    logger.error('Failed to initialize PostgreSQL connection', error as Error);
    throw error;
  }
}

/**
 * Close PostgreSQL database connection
 *
 * @param connection Database connection
 * @param logger Logger instance
 */
export async function closePostgresConnection(
  connection: DatabaseConnection,
  logger: Logger
): Promise<void> {
  try {
    // Cast to unknown first, then to Pool to access the end method
    const pool = connection.client as unknown as Pool;

    // End the pool
    await pool.end();

    logger.info('PostgreSQL connection closed successfully');
  } catch (error) {
    logger.error('Error closing PostgreSQL connection', error as Error);
    throw error;
  }
}
